import React, { useState, useEffect, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { GlobalContext } from "../contexts/GlobalContext";
import { useParams } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import Fab from "@mui/material/Fab";

// MUI Icons
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import ForestIcon from "@mui/icons-material/Forest";
import HistoryIcon from "@mui/icons-material/History";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupIcon from "@mui/icons-material/Group";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import SettingsIcon from "@mui/icons-material/Settings";
import HandShake from "@mui/icons-material/Handshake";
import SpaIcon from '@mui/icons-material/Spa';
import BusinessIcon from "@mui/icons-material/Business";
import  AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'

// Set drawer width - compact & modern
const drawerWidth = 195;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 0.75),
  minHeight: "40px",
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: open ? drawerWidth : theme.spacing(6.5) + 1,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      "& .MuiDrawer-paper": {
        position: "static",
        width: drawerWidth,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        boxSizing: "border-box",
        backgroundColor: "#f8f9fa",
        boxShadow: "2px 0 5px rgba(0, 0, 0, 0.06)",
        height: "100%",
        overflowY: "auto",
      },
    }),
    ...(!open && {
      "& .MuiDrawer-paper": {
        position: "static",
        width: `calc(${theme.spacing(6)} + 1px)`,
        [theme.breakpoints.up("sm")]: {
          width: `calc(${theme.spacing(6.5)} + 1px)`,
        },
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: "auto",
        backgroundColor: "#f8f9fa",
        boxShadow: "2px 0 5px rgba(0, 0, 0, 0.06)",
        height: "100%",
      },
    }),
  }),
);

// Main Dashboard component
const Dashboard = () => {
  const theme = useTheme();
  const [activeMenu, setActiveMenu] = useState("stats");
  const [open, setOpen] = useState(true);
  const { role } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { id, projectId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Combined drawer toggle function
  const handleDrawerToggle = () => {
    if (isMobile) {
      // For mobile, use the mobile drawer toggle
      handleMobileDrawerToggle();
    } else {
      // For desktop, toggle the permanent drawer
      setOpen(!open);
    }
  };

  // Mobile drawer toggle function
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    // This is critical - ensure text colors stay consistent
    // by not changing the 'open' state for the styled drawer component
  };

  useEffect(() => {
    // Set active menu based on URL
    const path = location.pathname.split("/").pop();
    switch (path) {
      case "stats":
        setActiveMenu("stats");
        break;
      case "profile":
        setActiveMenu("editProfile");
        break;
      case "mytrees":
        setActiveMenu("ProfilePosts");
        break;
      case "verifytrees":
        setActiveMenu("verifyPosts");
        break;
      case "createuser":
        setActiveMenu("viewUsers");
        break;
      case "viewusers":
        setActiveMenu("viewUsers");
        break;
      case "updategallery":
        setActiveMenu("updateGallery");
        break;
      case "projectdashboard":
        setActiveMenu("projectdashboard");
        break;
      case `${id}`:
        setActiveMenu("projectdashboard");
        break;
      case "newproject":
        setActiveMenu("projectdashboard");
        break;
      case "verifytreehistory":
        setActiveMenu("verifytreehistory");
        break;
      case "activity":
        setActiveMenu("activity");
        break;
      case "rewardpoints":
        setActiveMenu("rewardpoints");
        break;
      case "redeem":
        setActiveMenu("redeem");
        break;
      case "companyprofile":
        setActiveMenu("companyprofile");
        break;
      case `${projectId}`:
        setActiveMenu("projectdashboard");
        break;
      case "myemployees":
        setActiveMenu("myemployees");
        break;
      case "myspecies":
        setActiveMenu("myspecies");  
        break;
      case "companies":
        setActiveMenu("companies");
        break; 
      case "admin":
        setActiveMenu("admin");
        break;
      default:
        setActiveMenu("stats");
    }

    // Close drawer on mobile automatically
    if (isMobile) {
      setOpen(false);
    } 
    // else {
    //   setOpen(true);
    // }
  }, [location.pathname, id, projectId, isMobile]);

  const handleNavigation = (menu, path) => {
    setActiveMenu(menu);
    navigate(path);
    // Close drawer on mobile after navigation
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Menu items configuration
  const menuItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      menu: "stats",
      path: "./stats",
      roles: ["all"],
    },
    {
      text: "Profile",
      icon: <PersonIcon />,
      menu: "editProfile",
      path: "./profile",
      roles: ["all"],
    },
    {
      text: "My Trees",
      icon: <ForestIcon />,
      menu: "ProfilePosts",
      path: "./mytrees",
      roles: ["all"],
    },
    {
      text: role === "admin" ? "User Activity" : "My Activity",
      icon: <HistoryIcon />,
      menu: "activity",
      path: "./activity",
      roles: ["all"],
    },
    {
      text: role === "admin" ? "Redeemption Requests" : "Redeem Points",
      icon: <CardGiftcardIcon />,
      menu: "redeem",
      path: "./redeem",
      roles: ["all"],
    },
    {
      text: "Verify Trees",
      icon: <VerifiedIcon />,
      menu: "verifyPosts",
      path: "./verifytrees",
      roles: ["admin"],
    },
    {
      text: "Verify Tree History",
      icon: <HistoryIcon />,
      menu: "verifytreehistory",
      path: "./verifytreehistory",
      roles: ["admin"],
    },
    {
      text: "Manage Users",
      icon: <GroupIcon />,
      menu: "viewUsers",
      path: "./viewusers",
      roles: ["admin"],
    },
    {
      text: "Home Gallery",
      icon: <PhotoLibraryIcon />,
      menu: "updateGallery",
      path: "./updategallery",
      roles: ["admin"],
    },
    {
      text: "Reward Point Setting",
      icon: <SettingsIcon />,
      menu: "rewardpoints",
      path: "./rewardpoints",
      roles: ["admin"],
    },
    {
      text: "My Employees",
      icon: <GroupIcon />,
      menu: "myemployees",
      path: "./myemployees",
      roles: ["corporate", "ngo"],
    },
    {
      text: "Green Projects",
      icon: <HandShake />,
      menu: "projectdashboard",
      path: "./projectdashboard",
      roles: ["admin", "corporate", "ngo"],
    },
    {
      text:"Species Master",
      icon:<SpaIcon/>,
      menu:"myspecies",
      path:"./myspecies",
      roles:["admin"],
    },
     {
      text:"Companies Master",
      icon:<BusinessIcon/>,
      menu:"companies",
      path:"./companies",
      roles:["admin"],
    },
    {
        text:"Pagination Master",
      icon:<AdminPanelSettings/>,
      menu:"admin",
      path:"./admin",
      roles:["admin"],
    }
  ];

  // Create consistent drawer content for both mobile and desktop
  const createDrawerContent = (isMobileView) => (
    <>
      <DrawerHeader>
        <IconButton
          onClick={isMobileView ? handleMobileDrawerToggle : handleDrawerToggle}
          sx={{
            color: "#E70000",
            '&:hover': {
              bgcolor: "rgba(231, 0, 0, 0.08)",
            }
          }}
        >
          {isMobileView ?
            <ChevronLeftIcon /> :
            (open ? <ChevronLeftIcon /> : <ChevronRightIcon />)
          }
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List sx={{ p: "4px" }}>
        {menuItems.map((item) => {
          if (item.roles.includes("all") || item.roles.includes(role)) {
            return (
              <ListItem
                key={item.menu}
                disablePadding
                sx={{
                  display: "block",
                  mb: "2px",
                  borderRadius: "6px",
                  overflow: "hidden",
                  bgcolor: activeMenu === item.menu ? "#8BC34A" : "transparent",
                  "& span": {
                    color: activeMenu === item.menu ? "#FFFFFF" : "#37474F",
                  },
                  "& svg": {
                    color: activeMenu === item.menu ? "#FFFFFF" : "#37474F",
                  },
                  "&:hover": {
                    bgcolor: activeMenu === item.menu ? "#8BC34A" : "#E8F5E9",
                  },
                }}
              >
                <Tooltip title={(!isMobileView && !open) ? item.text : ""} placement="right">
                  <ListItemButton
                    sx={{
                      minHeight: 34,
                      py: 0.5,
                      px: 1.25,
                      justifyContent: (isMobileView || open) ? "initial" : "center",
                      color: activeMenu === item.menu ? "white" : "#191919",
                      "&:hover": {
                        color: activeMenu === item.menu ? "white" : "#191919",
                      },
                    }}
                    onClick={() => handleNavigation(item.menu, item.path)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: (isMobileView || open) ? 1.25 : "auto",
                        justifyContent: "center",
                        color: activeMenu === item.menu ? "white" : "#E70000",
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.1rem",
                        },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: (isMobileView || open) ? 1 : 0,
                        m: 0,
                        "& .MuiTypography-root": {
                          fontWeight: activeMenu === item.menu ? 700 : 500,
                          fontSize: "12px",
                          lineHeight: 1.2,
                          letterSpacing: "-0.01em",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          }
          return null;
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ 
      display: "flex", 
      height: "calc(100vh - 64px)", /* Adjust based on navbar height */
      width: "100%"  // Ensure full width
    }}>
      {/* Desktop drawer - note the position: "relative" is part of the problem */}
      {!isMobile && (
        <Drawer 
          variant="permanent" 
          open={open}
          sx={{
            position: "relative",  // This position needs to be changed
            zIndex: 1,
            flexShrink: 0,  // Don't allow the drawer to shrink
          }}
        >
          {createDrawerContent(false)}
        </Drawer>
      )}
  
      {/* Mobile drawer - this is fine as temporary */}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: "#f8f9fa",
              overflowY: "auto",
            },
          }}
        >
          {createDrawerContent(true)}
        </MuiDrawer>
      )}
  
      {/* Mobile menu toggle button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="open menu"
          size="small"
          onClick={handleMobileDrawerToggle}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: 1200,
            bgcolor: "#E70000",
            '&:hover': {
              bgcolor: "#c62828",
            }
          }}
        >
          <MenuIcon />
        </Fab>
      )}
  
      {/* Main content area - this needs to grow properly */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,  // This is correct - it will take remaining space
          p: 3,
          bgcolor: "#f9f9f9",
          height: "100%",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          width: { 
            sm: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${theme.spacing(8)} - 1px)`,
            xs: "100%" // On mobile, take full width
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%' 
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;